import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Play, Clock, Calendar, Film, 
  Search, Grid, List, Heart
} from "lucide-react";
import { Button } from "./ui/button";
import { ShareButtons } from "./ShareButtons";
import { InFeedAd } from "./AdUnit";

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RecordingsPage = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [sportFilter, setSportFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState(new Set());
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch only PUBLISHED recordings for public On Demand page
        const response = await axios.get(`${API}/recordings/on-demand`);
        setRecordings(response.data);
        
        // Check if user is logged in and fetch their favorites
        const token = localStorage.getItem("userToken");
        if (token) {
          setIsLoggedIn(true);
          try {
            const favRes = await axios.get(`${API}/users/favorites`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const favIds = (favRes.data.favorites || []).map(f => f.id);
            setFavorites(new Set(favIds));
          } catch (e) {
            console.error("Error fetching favorites:", e);
          }
        }
      } catch (error) {
        console.error("Error fetching on-demand content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleFavorite = async (recordingId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem("userToken");
    if (!token) {
      // Redirect to login
      window.location.href = `/login-user?return=/on-demand`;
      return;
    }

    try {
      if (favorites.has(recordingId)) {
        await axios.delete(`${API}/users/favorites/${recordingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(recordingId);
          return next;
        });
      } else {
        await axios.post(`${API}/users/favorites`, 
          { recording_id: recordingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavorites(prev => new Set([...prev, recordingId]));
      }
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  const sports = ["all", ...new Set(recordings.map(r => r.sport).filter(Boolean))];

  const filteredRecordings = recordings.filter(recording => {
    const matchesSport = sportFilter === "all" || recording.sport === sportFilter;
    const matchesSearch = !searchQuery || 
      recording.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recording.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSport && matchesSearch;
  });

  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <main data-testid="on-demand-page" className="min-h-screen bg-[#050505]">
      
      

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 pt-8">
        {/* Page Title */}
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Film className="w-8 h-8 text-white" />
            <h1 className="font-secondary text-3xl font-bold uppercase tracking-tight text-white">On Demand</h1>
          </div>
          <ShareButtons title="ASN On Demand - Watch replays and highlights" variant="dropdown" />
        </div>
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search on-demand content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-zinc-900 border border-white/10 text-white pl-10 pr-4 py-3 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
              data-testid="search-on-demand"
            />
          </div>

          {/* Sport Filter */}
          <div className="flex gap-2 flex-wrap">
            {sports.map(sport => (
              <button
                key={sport}
                onClick={() => setSportFilter(sport)}
                className={`px-4 py-2 font-secondary text-xs font-bold uppercase tracking-widest transition-colors ${
                  sportFilter === sport
                    ? "bg-white text-black"
                    : "bg-zinc-900 text-zinc-400 hover:text-white border border-white/10"
                }`}
                data-testid={`filter-${sport}`}
              >
                {sport === "all" ? "All Sports" : sport}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 bg-zinc-900 border border-white/10 p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 transition-colors ${viewMode === "grid" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 transition-colors ${viewMode === "list" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-zinc-500 mb-6">
          {filteredRecordings.length} video{filteredRecordings.length !== 1 ? 's' : ''} available
        </p>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredRecordings.length === 0 && (
          <div className="text-center py-20">
            <Film className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-secondary text-xl font-bold uppercase tracking-tight text-white mb-2">No Content Available</h2>
            <p className="text-zinc-500">Check back later for on-demand videos</p>
          </div>
        )}

        {/* Grid View */}
        {!loading && viewMode === "grid" && filteredRecordings.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
          >
            {filteredRecordings.map((recording, index) => (
              <React.Fragment key={recording.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                  data-testid={`recording-card-${recording.id}`}
                >
                  <Link to={`/on-demand/${recording.id}`}>
                    <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                      {recording.thumbnail_url ? (
                        <img
                          src={recording.thumbnail_url}
                          alt={recording.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-12 h-12 text-zinc-700" />
                        </div>
                      )}
                      
                      {/* Play Overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-black ml-1" fill="currentColor" />
                        </div>
                      </div>

                      {/* Favorite Button */}
                      <button
                        onClick={(e) => toggleFavorite(recording.id, e)}
                        className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
                          favorites.has(recording.id) 
                            ? 'bg-red-500 text-white' 
                            : 'bg-black/60 text-white hover:bg-black/80'
                        }`}
                        data-testid={`favorite-${recording.id}`}
                      >
                        <Heart className={`w-4 h-4 ${favorites.has(recording.id) ? 'fill-current' : ''}`} />
                      </button>

                      {/* Duration Badge */}
                      {recording.duration_seconds > 0 && (
                        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 text-xs text-white font-mono">
                          {formatDuration(recording.duration_seconds)}
                        </div>
                      )}

                      {/* Featured Badge */}
                      {recording.is_featured && (
                        <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest">
                          Featured
                        </div>
                      )}
                    </div>

                    <div className="pt-2">
                      <h3 className="font-secondary text-sm font-bold uppercase tracking-wide text-white group-hover:text-zinc-300 transition-colors line-clamp-2">
                        {recording.title}
                      </h3>

                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                        {recording.sport && (
                          <span className="uppercase tracking-widest">{recording.sport}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(recording.recorded_at)}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
                {/* Insert ad after every 8th video */}
                {(index + 1) % 8 === 0 && index < filteredRecordings.length - 1 && (
                  <div className="col-span-full">
                    <InFeedAd slot="YOUR_ONDEMAND_AD_SLOT" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </motion.div>
        )}

        {/* List View */}
        {!loading && viewMode === "list" && filteredRecordings.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-0 border border-white/5"
          >
            {filteredRecordings.map((recording, index) => (
              <motion.div
                key={recording.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                data-testid={`recording-list-item-${recording.id}`}
                className="border-b border-white/5 last:border-b-0"
              >
                <Link 
                  to={`/on-demand/${recording.id}`}
                  className="flex gap-4 p-4 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors group"
                >
                  {/* Thumbnail */}
                  <div className="relative w-48 aspect-video bg-zinc-800 flex-shrink-0 overflow-hidden">
                    {recording.thumbnail_url ? (
                      <img
                        src={recording.thumbnail_url}
                        alt={recording.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-8 h-8 text-zinc-700" />
                      </div>
                    )}
                    {recording.duration_seconds > 0 && (
                      <div className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 text-[10px] text-white font-mono">
                        {formatDuration(recording.duration_seconds)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-secondary text-base font-bold uppercase tracking-wide text-white group-hover:text-zinc-300 transition-colors">
                          {recording.title}
                        </h3>
                        {recording.description && (
                          <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{recording.description}</p>
                        )}
                      </div>
                      {recording.is_featured && (
                        <span className="bg-yellow-500 text-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest flex-shrink-0">
                          Featured
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                      {recording.sport && (
                        <span className="uppercase tracking-widest bg-zinc-800 px-2 py-1">{recording.sport}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(recording.recorded_at)}
                      </span>
                    </div>
                  </div>

                  {/* Favorite Button */}
                  <button
                    onClick={(e) => toggleFavorite(recording.id, e)}
                    className={`p-2 self-center transition-all ${
                      favorites.has(recording.id) 
                        ? 'text-red-500' 
                        : 'text-zinc-500 hover:text-white'
                    }`}
                    data-testid={`favorite-list-${recording.id}`}
                  >
                    <Heart className={`w-5 h-5 ${favorites.has(recording.id) ? 'fill-current' : ''}`} />
                  </button>

                  {/* Play Button */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white/10 group-hover:bg-white rounded-full flex items-center justify-center transition-colors">
                      <Play className="w-5 h-5 text-white group-hover:text-black ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <div className="noise-overlay" aria-hidden="true" />
    </main>
  );
};

export default RecordingsPage;
